import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ScoreIcon from '@mui/icons-material/Score';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Navbar from '../components/Navbar/Navbar';
import { Base_URL } from '../Client/apiURL';
import axios from 'axios';
import ListeningTestUI from '../components/IELTS/ListeningTestUI';
import ReadingTestUI from '../components/IELTS/ReadingTestUI';
import WritingTestUI from '../components/IELTS/WritingTestUI';
import SpeakingTestUI from '../components/IELTS/SpeakingTestUI';
import IELTSDetailedScoring from '../components/IELTS/IELTSDetailedScoring';
import { hasAIScore, hasScore, shouldShowScore, getBandScore, getAIScoring, parseAnalytics } from '../components/IELTS/utils/scoreHelpers';

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: '120px',
  paddingBottom: '4rem',
  minHeight: '100vh',
  [theme.breakpoints.down('md')]: {
    paddingTop: '100px',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    paddingBottom: '3rem',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,
  textTransform: 'none',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 20px',
    fontSize: '14px',
  },
}));

const ResultsBanner = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #996CFE 0%, #7C4DFF 100%)',
  color: 'white',
  marginBottom: '2rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(153, 108, 254, 0.3)',
  overflow: 'visible',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    borderRadius: '16px',
    pointerEvents: 'none',
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.15)',
  borderRadius: '12px',
  padding: '16px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.down('sm')]: {
    padding: '12px',
  },
}));

const ScoreCircle = styled(Box)(({ theme }) => ({
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  background: 'rgba(255, 255, 255, 0.2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '24px',
  fontWeight: 'bold',
  border: '3px solid rgba(255, 255, 255, 0.3)',
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    height: '60px',
    fontSize: '18px',
  },
}));

const TestScoresBanner = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #F8F2FF 0%, #E8DFFF 100%)',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '2rem',
  boxShadow: '0 4px 20px rgba(153, 108, 254, 0.1)',
  border: '1px solid rgba(153, 108, 254, 0.2)',
  [theme.breakpoints.down('sm')]: {
    padding: '15px',
  },
}));

const ScoreItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: '86px',
  padding: '12px',
  borderRadius: '8px',
  boxSizing: 'border-box',
  background: 'rgba(255, 255, 255, 0.7)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(153, 108, 254, 0.2)',
  },
}));

const IELTSTestDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [availableTests, setAvailableTests] = useState({});
  
  const attemptId = searchParams.get('attemptId');
  const groupId = searchParams.get('groupId');
  const fullTestAttemptId = searchParams.get('fullTestAttemptId');
  const type = searchParams.get('type'); // 'individual', 'group', or 'full'
  const isAdminMode = searchParams.get('user') === 'instructor';
  const adminUserId = isAdminMode ? searchParams.get('userId') : null;
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [resumeFullTestLoading, setResumeFullTestLoading] = useState(false);

  useEffect(() => {
    if (type === 'individual' && attemptId) {
      fetchIndividualTestDetails(attemptId);
    } else if (type === 'group' && groupId) {
      fetchGroupDetails(groupId);
    } else if (type === 'full' && fullTestAttemptId) {
      fetchFullTestDetails(fullTestAttemptId);
    } else {
      setError('Invalid parameters provided');
      setLoading(false);
    }
  }, [attemptId, groupId, fullTestAttemptId, type]);

  // Reset speaking part to 1 when switching to Speaking tab
  useEffect(() => {
    if (selectedTestType === 'Speaking') {
    }
  }, [selectedTestType]);

  // Process available tests and auto-select when data is loaded
  useEffect(() => {
    if (!detailsData || !detailsData.response) return;

    const processedTests = (() => {
      if (type === 'individual') {
        // For individual tests, the structure is response.users[0].tests[0]
        if (detailsData.response.users && detailsData.response.users.length > 0) {
          const tests = detailsData.response.users[0].tests;
          if (tests && tests.length > 0) {
            return { test: tests[0] }; // Take the first (and only) test
          }
        }
        // Fallback: check if there's a direct test property
        if (detailsData.response.test) {
          return { test: detailsData.response.test };
        }
        return {};
      } else if (type === 'group' || type === 'full') {
        // Check if we have users array like in the original modal
        if (detailsData.response.users && detailsData.response.users.length > 0) {
          const tests = detailsData.response.users[0].tests;
          if (tests && tests.length > 0) {
            const hasAttemptData = (test) => {
              if (!test) return false;
              if (Array.isArray(test.attempts_summary) && test.attempts_summary.length > 0) {
                return true;
              }
              if (Array.isArray(test.sections)) {
                return test.sections.some(
                  (section) => Array.isArray(section.attempts) && section.attempts.length > 0
                );
              }
              return false;
            };

            const pickByCategory = (categoryName) => {
              const categoryTests = tests.filter((t) => t.category_name === categoryName);
              if (categoryTests.length === 0) return null;

              // Prefer the row that actually contains attempt data.
              const attempted = categoryTests.find((t) => hasAttemptData(t));
              if (attempted) return attempted;

              // Fallback to first item (keeps pending/skipped rendering for untouched categories).
              return categoryTests[0];
            };

            const listeningTest = pickByCategory('Listening');
            const readingTest = pickByCategory('Reading');
            const writingTest = pickByCategory('Writing');
            const speakingTest = pickByCategory('Speaking');

            return { listeningTest, readingTest, writingTest, speakingTest };
          }
        }
        return {};
      }
      return {};
    })();

    setAvailableTests(processedTests);

    // Auto-select test type if only one is available or if none is selected
    if (!selectedTestType) {
      const { listeningTest, readingTest, writingTest, speakingTest } = processedTests;
      
      if (listeningTest && !readingTest && !writingTest && !speakingTest) {
        setSelectedTestType('Listening');
      } else if (readingTest && !listeningTest && !writingTest && !speakingTest) {
        setSelectedTestType('Reading');
      } else if (writingTest && !listeningTest && !readingTest && !speakingTest) {
        setSelectedTestType('Writing');
      } else if (speakingTest && !listeningTest && !readingTest && !writingTest) {
        setSelectedTestType('Speaking');
      } else if (processedTests.test) {
        // For individual tests, determine type from the test data
        const testType = processedTests.test.category_name || processedTests.test.test_type;
        setSelectedTestType(testType);
      } else if (listeningTest) {
        // Default to first available test
        setSelectedTestType('Listening');
      } else if (readingTest) {
        setSelectedTestType('Reading');
      } else if (writingTest) {
        setSelectedTestType('Writing');
      } else if (speakingTest) {
        setSelectedTestType('Speaking');
      }
    }
  }, [detailsData, type, selectedTestType]);

  const fetchIndividualTestDetails = async (attemptId) => {
    try {
      setLoading(true);
      setError(null);
      
      let url;
      const withCredentials = isAdminMode && adminUserId ? false : true;

      if (isAdminMode && adminUserId) {
        url = `${Base_URL}/app/admin/ielts-tests/getCompletedTestByAttemptId/${attemptId}?userId=${adminUserId}`;
      } else {
        url = `${Base_URL}/app/users/ielts-tests/getCompletedTestByAttemptId/${attemptId}`;
      }

      const response = await axios.get(url, { withCredentials });
      setDetailsData(response.data);
    } catch (err) {
      console.error('Error fetching individual test details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load test details');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupDetails = async (groupId) => {
    try {
      setLoading(true);
      setError(null);
      
      let url;
      const withCredentials = isAdminMode && adminUserId ? false : true;

      if (isAdminMode && adminUserId) {
        url = `${Base_URL}/app/admin/ielts-tests/getTestsByGroupId/${groupId}?userId=${adminUserId}`;
      } else {
        url = `${Base_URL}/app/users/ielts-tests/getTestsByGroupId/${groupId}`;
      }

      const response = await axios.get(url, { withCredentials });
      setDetailsData(response.data);
    } catch (err) {
      console.error('Error fetching group details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load group details');
    } finally {
      setLoading(false);
    }
  };

  const fetchFullTestDetails = async (fullTestAttemptId) => {
    try {
      setLoading(true);
      setError(null);
      
      let url;
      const withCredentials = isAdminMode && adminUserId ? false : true;

      if (isAdminMode && adminUserId) {
        url = `${Base_URL}/app/admin/ielts-tests/getTestsByFullAttemptId/${fullTestAttemptId}?userId=${adminUserId}`;
      } else {
        url = `${Base_URL}/app/users/ielts-tests/getTestsByFullAttemptId/${fullTestAttemptId}`;
      }

      const response = await axios.get(url, { withCredentials });
      setDetailsData(response.data);
    } catch (err) {
      console.error('Error fetching full test details:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load full test details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToResults = () => {
    if (isAdminMode && adminUserId) {
      // Admin mode: navigate to admin results page
      window.location.href = `${Base_URL}/app/admin/ielts-results/${adminUserId}`;
    } else {
      navigate('/ielts-results');
    }
  };

  const handleReturnToDashboard = () => {
    if (isAdminMode) {
      // Admin mode: navigate to admin students list
      window.location.href = `${Base_URL}/app/admin/students`;
    } else {
      navigate('/ielts-dashboard');
    }
  };

  const navigateToFullTestResume = (payload) => {
    if (!payload?.tests?.length) return;
    const sectionOrder = ['Listening', 'Reading', 'Writing', 'Speaking'];
    const pickCategoryRow = (categoryName) => {
      const rows = payload.tests.filter((t) => t.category_name === categoryName);
      if (rows.length === 0) return null;
      const completedRow = rows.find((t) => t.category_attempt_status === 'completed');
      if (completedRow) return completedRow;
      const pendingRow = rows.find((t) => t.category_attempt_status === 'pending_attempt');
      return pendingRow || rows[0];
    };
    const groupedTests = sectionOrder
      .map((categoryName) => pickCategoryRow(categoryName))
      .filter(Boolean)
      .map((t) => ({
        id: t.id,
        category_name: t.category_name,
        groupId: payload.groupId,
        group_name: t.group_name,
        test_type: t.test_type,
        audio_url: t.audio_url,
        sections: t.sections || [],
      }));
    const prefetchedResumeContext = {
      ...payload,
      fullTestAttemptId: Number(fullTestAttemptId),
    };
    navigate('/ielts-full-test', {
      state: {
        mode: 'practice',
        groupedTests,
        groupId: payload.groupId,
        continuePrevious: true,
        resumeFullTestAttemptId: Number(fullTestAttemptId),
        prefetchedResumeContext,
      },
    });
  };

  const handleResumeFullTest = async () => {
    if (!fullTestAttemptId) return;
    setError(null);
    const embedded = detailsData?.response?.users?.[0]?.resumeContext;
    if (embedded?.tests?.length) {
      navigateToFullTestResume(embedded);
      return;
    }
    setResumeFullTestLoading(true);
    try {
      const response = await fetch(
        `${Base_URL}/app/users/ielts-tests/full-attempts/${fullTestAttemptId}/resume-context`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        }
      );
      const data = await response.json();
      const payload = data?.response;
      if (!response.ok || data?.responseCode !== 200 || !payload?.tests?.length) {
        throw new Error(data?.message || 'Unable to load full test for resume');
      }
      navigateToFullTestResume(payload);
    } catch (error) {
      console.error('Failed to resume full test:', error);
      setError(error.message || 'Unable to resume full test');
    } finally {
      setResumeFullTestLoading(false);
    }
  };

  const getTestTitle = () => {
    if (type === 'individual') return 'Individual Test Details';
    if (type === 'group') return `Group ${groupId} Test Details`;
    if (type === 'full') return 'Full Test Details';
    return 'Test Details';
  };

  const canDeleteResult = (type === 'individual' && attemptId) || (type === 'full' && fullTestAttemptId);

  const handleDeleteResultClick = () => setDeleteConfirm(true);
  const handleCloseDeleteConfirm = () => { if (!deleting) setDeleteConfirm(false); };

  const handleConfirmDeleteResult = async () => {
    if (!canDeleteResult) return;
    setDeleting(true);
    try {
      const isIndividual = type === 'individual';
      const id = isIndividual ? attemptId : fullTestAttemptId;
      const path = `${Base_URL}/app/users/ielts-tests/result/${isIndividual ? 'individual' : 'full'}/${id}`;
      const res = await fetch(path, { method: 'DELETE', credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.responseCode === 200) {
        setDeleteConfirm(false);
        handleBackToResults();
      } else {
        setError(data.message || 'Failed to delete result');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete result');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 'N/A';
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatDurationFromMinutes = (totalMinutes) => {
    if (totalMinutes === undefined || totalMinutes === null) return 'N/A';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const calculateOverallStats = () => {
    if (!detailsData || !detailsData.response) return null;

    let totalScore = 0;
    let totalQuestions = 0;
    let correctAnswers = 0;
    let testCount = 0;
    let totalDurationMinutes = 0;
    let allTestNames = [];
    let userData = null;
    let completionDate = null;
    let hasPendingAttempts = false;
    const sectionAccuracyStats = {
      Listening: { correct: 0, total: 0, hasData: false },
      Reading: { correct: 0, total: 0, hasData: false }
    };

    // Handle both individual and full test data structures
    if (type === 'individual') {
      // For individual test - handle actual API structure
      let testData = null;
      
      if (detailsData.response.users && detailsData.response.users.length > 0) {
        const tests = detailsData.response.users[0].tests;
        if (tests && tests.length > 0) {
          testData = tests[0]; // Get first test from users array
        }
      }
      
      if (!testData || !testData.attempts_summary || testData.attempts_summary.length === 0) return null;
      
      const latestAttempt = testData.attempts_summary[testData.attempts_summary.length - 1];
      
      // Calculate correct/incorrect from analytics
      if (latestAttempt.analytics) {
        Object.values(latestAttempt.analytics).forEach(answer => {
          if (typeof answer === 'object' && answer.hasOwnProperty('isCorrect')) {
            totalQuestions++;
            if (answer.isCorrect) correctAnswers++;
          }
        });
      }
      
      totalScore = parseFloat(latestAttempt.totalMarksObtained || 0);
      testCount = 1;
      
      // Use duration from API response (in minutes)
      if (latestAttempt.duration !== undefined) {
        totalDurationMinutes = latestAttempt.duration;
      }
      if (testData.test_name) allTestNames.push(testData.test_name);
      
      // Get completion date from latest attempt
      if (latestAttempt.end_time) {
        completionDate = new Date(latestAttempt.end_time);
      }
      
      userData = detailsData.response.users[0] || {};
    } else {
      // For full test
      if (!detailsData.response.users) return null;
      userData = detailsData.response.users[0];
      if (!userData || !userData.tests) return null;
      const isSpeakingOptionalForAttempt =
        !!userData?.isSpeakingOptional || !!userData?.fullTestAttempt?.is_speaking_optional;

      // Get completion date from fullTestAttempt
      if (userData.fullTestAttempt && userData.fullTestAttempt.end_time) {
        completionDate = new Date(userData.fullTestAttempt.end_time);
      }

      userData.tests.forEach(test => {
        const testType = test.category_name || test.test_type;
        if (
          test.category_attempt_status === 'pending_attempt' &&
          !(isSpeakingOptionalForAttempt && testType === 'Speaking')
        ) {
          hasPendingAttempts = true;
        }

        let attemptData = null;
        let analyticsData = null;
        let marksObtained = null;
        let duration = null;

        // First, try to get data from attempts_summary
        if (test.attempts_summary && test.attempts_summary.length > 0) {
          attemptData = test.attempts_summary[0];
          analyticsData = attemptData.analytics;
          marksObtained = attemptData.totalMarksObtained;
          duration = attemptData.duration;
        } else {
          // Fallback: Check section-level attempts
          if (test.sections && test.sections.length > 0) {
            for (const section of test.sections) {
              if (section.attempts && section.attempts.length > 0) {
                const sectionAttempt = section.attempts[0];
                if (!attemptData || new Date(sectionAttempt.created_at) > new Date(attemptData.created_at)) {
                  attemptData = sectionAttempt;
                  // Try to parse analytics from section attempt
                  if (sectionAttempt.analytics) {
                    try {
                      analyticsData = typeof sectionAttempt.analytics === 'string' 
                        ? JSON.parse(sectionAttempt.analytics) 
                        : sectionAttempt.analytics;
                    } catch (e) {
                      analyticsData = sectionAttempt.analytics;
                    }
                  }
                  marksObtained = sectionAttempt.total_marks_obtained;
                  // Calculate duration from section attempt
                  if (sectionAttempt.start_time && sectionAttempt.end_time) {
                    const startTime = new Date(sectionAttempt.start_time);
                    const endTime = new Date(sectionAttempt.end_time);
                    const durationMs = endTime.getTime() - startTime.getTime();
                    duration = Math.round(durationMs / (1000 * 60));
                  }
                }
              }
            }
          }
        }

        // Process analytics if available
        if (analyticsData) {
          let sectionTotalQuestions = 0;
          let sectionCorrectAnswers = 0;
          Object.values(analyticsData).forEach(answer => {
            if (typeof answer === 'object' && answer.hasOwnProperty('isCorrect')) {
              totalQuestions++;
              if (answer.isCorrect) correctAnswers++;
              sectionTotalQuestions++;
              if (answer.isCorrect) sectionCorrectAnswers++;
            }
          });

          if (
            (testType === 'Listening' || testType === 'Reading') &&
            sectionTotalQuestions > 0
          ) {
            sectionAccuracyStats[testType].correct += sectionCorrectAnswers;
            sectionAccuracyStats[testType].total += sectionTotalQuestions;
            sectionAccuracyStats[testType].hasData = true;
          }
        }
        
        // Add marks if available (skip Writing/Speaking with pending_evaluation status)
        if (marksObtained && attemptData && attemptData.status !== 'pending_evaluation') {
          totalScore += parseFloat(marksObtained);
        }
        
        // Add duration if available
        if (duration !== null && duration !== undefined) {
          totalDurationMinutes += duration;
        }
        
        // Count test if it has attempt data
        if (attemptData) {
          testCount++;
        }
        
        if (test.test_name) {
          allTestNames.push(test.test_name);
        }
      });
    }

    const accuracy = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    const avgScore = testCount > 0 ? (totalScore / testCount).toFixed(1) : '0';

    return {
      totalScore: totalScore.toFixed(1),
      avgScore,
      accuracy,
      totalQuestions,
      correctAnswers,
      hasPendingAttempts,
      sectionAccuracyStats,
      testCount,
      duration: formatDurationFromMinutes(totalDurationMinutes),
      testNames: allTestNames,
      userName: userData.user?.name || 'Unknown User',
      userEmail: userData.user?.email || '',
      completionDate: completionDate
    };
  };

  const renderResultsBanner = () => {
    const stats = calculateOverallStats();
    if (!stats) return null;
    const completedAccuracySections = type === 'full'
      ? ['Listening', 'Reading'].filter((sectionName) => stats.sectionAccuracyStats?.[sectionName]?.hasData)
      : [];

    return (
      <ResultsBanner>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              {type === 'full' ? 'Full Test Results' : `${selectedTestType} Test Results`}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mb: 2 }}>
              {stats.userName}
            </Typography>

          </Box>

          <Grid container spacing={3}>
            <Grid item xs={6} sm={3}>
              <StatCard>
              {stats.hasPendingAttempts ? (
                <Typography variant="body" >
                  Complete pending attempts to view your total score.
                </Typography>
              ) : (
                <>
                  <ScoreCircle>
                    {stats.avgScore}
                  </ScoreCircle>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Score
                  </Typography>
                </>              
              )}
              </StatCard>
            </Grid>

                         <Grid item xs={6} sm={3}>
               <StatCard>
                 <Box sx={{ position: 'relative', display: 'inline-flex', mb: 1 }}>
                   <CircularProgress
                     variant="determinate"
                     value={stats.accuracy}
                     size={60}
                     thickness={4}
                     sx={{
                       color: 'rgba(255, 255, 255, 0.8)',
                       '& .MuiCircularProgress-circle': {
                         strokeLinecap: 'round',
                       },
                     }}
                   />
                   <Box
                     sx={{
                       top: 0,
                       left: 0,
                       bottom: 0,
                       right: 0,
                       position: 'absolute',
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                     }}
                   >
                     <Typography variant="h6" sx={{ fontWeight: 700 }}>
                       {stats.accuracy}%
                     </Typography>
                   </Box>
                 </Box>
                 <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                   Accuracy
                 </Typography>
                 {type === 'full' && (
                   <Typography variant="caption" sx={{ opacity: 0.8, mb: 1, display: 'block', fontStyle: 'italic' }}>
                     (Listening & Reading only)
                   </Typography>
                 )}
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                   {type === 'full' ? (
                     completedAccuracySections.length > 0 ? (
                      completedAccuracySections.map((sectionName) => {
                        const sectionStats = stats.sectionAccuracyStats[sectionName];
                        const incorrectCount = sectionStats.total - sectionStats.correct;
                        return (
                          <Box key={sectionName} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 0.5, px: 1 }}>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                              {sectionName}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                ✓ {sectionStats.correct}
                              </Typography>
                              <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                                ✗ {incorrectCount}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })
                     ) : (
                       <Typography variant="caption" sx={{ opacity: 0.7 }}>
                         No Listening/Reading attempts yet
                       </Typography>
                     )
                   ) : (
                     <>
                       <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                         ✓ {stats.correctAnswers} Correct
                       </Typography>
                       <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500 }}>
                         ✗ {stats.totalQuestions - stats.correctAnswers} Incorrect
                       </Typography>
                       <Typography variant="caption" sx={{ opacity: 0.7 }}>
                         Total: {stats.totalQuestions} questions
                       </Typography>
                     </>
                   )}
                 </Box>
               </StatCard>
             </Grid>

            <Grid item xs={6} sm={3}>
              <StatCard>
                <AccessTimeIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Duration
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {stats.duration}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Time taken
                </Typography>
              </StatCard>
            </Grid>

            <Grid item xs={6} sm={3}>
              <StatCard>
                <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Tests
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {stats.testCount}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Completed
                </Typography>
              </StatCard>
            </Grid>
          </Grid>

          {stats.completionDate ? (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <Typography variant="body2" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                Completed on {formatDate(stats.completionDate)}
              </Typography>
            </Box>
          ) : stats.hasPendingAttempts ? (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <Typography variant="body2" sx={{ opacity: 0.8, display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CalendarTodayIcon sx={{ fontSize: 16 }} />
                There are some sections with pending attempts. Please complete those to see the total score.
              </Typography>
            </Box>
          ) : null}
        </CardContent>
      </ResultsBanner>
    );
  };

  const renderTestContent = () => {
    if (!detailsData || !detailsData.response) {
      return <Typography>No data received from server</Typography>;
    }

    if (!selectedTestType || !availableTests) {
      return <Typography>Loading test data...</Typography>;
    }



    // Get the test to show based on selected type
    const testToShow = (() => {
      if (type === 'individual') {
        return availableTests.test;
      } else {
        if (selectedTestType === 'Listening') return availableTests.listeningTest;
        if (selectedTestType === 'Reading') return availableTests.readingTest;
        if (selectedTestType === 'Writing') return availableTests.writingTest;
        if (selectedTestType === 'Speaking') return availableTests.speakingTest;
      }
      return null;
    })();

    if (!testToShow) {
      return <Typography>No test data available for {selectedTestType}</Typography>;
    }

    // Determine test type
    const testType = testToShow.category_name || testToShow.test_type || selectedTestType;
    
          return (
        <>
          {renderResultsBanner()}
          
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#996CFE' }}>
            {selectedTestType} Test Review
          </Typography>
        
        {/* Test Type Selector - only show if multiple tests are available */}
        {availableTests && (
          (availableTests.listeningTest || availableTests.readingTest || availableTests.writingTest || availableTests.speakingTest) && 
          ((availableTests.listeningTest ? 1 : 0) + (availableTests.readingTest ? 1 : 0) + (availableTests.writingTest ? 1 : 0) + (availableTests.speakingTest ? 1 : 0)) > 1 && (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              {availableTests.listeningTest && (
                <Button
                  variant={selectedTestType === 'Listening' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedTestType('Listening')}
                  sx={{ 
                    backgroundColor: selectedTestType === 'Listening' ? '#996CFE' : 'transparent',
                    borderColor: '#996CFE',
                    color: selectedTestType === 'Listening' ? 'white' : '#996CFE',
                    '&:hover': { 
                      backgroundColor: selectedTestType === 'Listening' ? '#7C4DFF' : '#F5F0FF' 
                    }
                  }}
                >
                  Listening
                </Button>
              )}
              {availableTests.readingTest && (
                <Button
                  variant={selectedTestType === 'Reading' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedTestType('Reading')}
                  sx={{ 
                    backgroundColor: selectedTestType === 'Reading' ? '#996CFE' : 'transparent',
                    borderColor: '#996CFE',
                    color: selectedTestType === 'Reading' ? 'white' : '#996CFE',
                    '&:hover': { 
                      backgroundColor: selectedTestType === 'Reading' ? '#7C4DFF' : '#F5F0FF' 
                    }
                  }}
                >
                    Reading
                  </Button>
              )}
              {availableTests.writingTest && (
                <Button
                  variant={selectedTestType === 'Writing' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedTestType('Writing')}
                  sx={{ 
                    backgroundColor: selectedTestType === 'Writing' ? '#996CFE' : 'transparent',
                    borderColor: '#996CFE',
                    color: selectedTestType === 'Writing' ? 'white' : '#996CFE',
                    '&:hover': { 
                      backgroundColor: selectedTestType === 'Writing' ? '#7C4DFF' : '#F5F0FF' 
                    }
                  }}
                >
                  Writing
                </Button>
              )}
              {availableTests.speakingTest && (
                <Button
                  variant={selectedTestType === 'Speaking' ? 'contained' : 'outlined'}
                  onClick={() => setSelectedTestType('Speaking')}
                  sx={{ 
                    backgroundColor: selectedTestType === 'Speaking' ? '#996CFE' : 'transparent',
                    borderColor: '#996CFE',
                    color: selectedTestType === 'Speaking' ? 'white' : '#996CFE',
                    '&:hover': { 
                      backgroundColor: selectedTestType === 'Speaking' ? '#7C4DFF' : '#F5F0FF' 
                    }
                  }}
                >
                  Speaking
                </Button>
              )}
            </Box>
          )
        )}
        
        {selectedTestType === 'Listening' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
            {(!testToShow.attempts_summary || testToShow.attempts_summary.length === 0) && 
             (!testToShow.sections || testToShow.sections.every(s => !s.attempts || s.attempts.length === 0)) && (
              <Box sx={{ mb: 3, p: 2, borderRadius: '8px', backgroundColor: '#FFF3E0', border: '1px solid #FF9800' }}>
                <Typography variant="body1" sx={{ color: '#F57C00', fontWeight: 500 }}>
                  No attempt data found for this Listening test. If you completed this test, please contact support.
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 2, width: '100%' }}>
              <ListeningTestUI
              testData={testToShow}
              sections={testToShow.sections || []}
              timeLimit={30}
              mode="review"
              onSubmit={() => {}}
              onTimeUp={() => {}}
              testId={testToShow.id || null}
              userId={detailsData.response.users && detailsData.response.users[0] ? detailsData.response.users[0].userId || detailsData.response.users[0].user_id : null}
              apiResult={(() => {
                // Create apiResult structure from attempts_summary for review mode
                if (testToShow.attempts_summary && testToShow.attempts_summary.length > 0) {
                  const latestAttempt = testToShow.attempts_summary[0];
                  return {
                    response: {
                      analytics: latestAttempt.analytics || {},
                      totalMarksObtained: latestAttempt.totalMarksObtained,
                      status: latestAttempt.status
                    }
                  };
                }
                // Fallback: try to get from section attempts - collect from all sections
                if (testToShow.sections && testToShow.sections.length > 0) {
                  // Find the latest attempt across all sections
                  let latestSectionAttempt = null;
                  let latestAttemptDate = null;
                  
                  for (const section of testToShow.sections) {
                    if (section.attempts && section.attempts.length > 0) {
                      for (const attempt of section.attempts) {
                        const attemptDate = new Date(attempt.created_at || attempt.updated_at);
                        if (!latestAttemptDate || attemptDate > latestAttemptDate) {
                          latestAttemptDate = attemptDate;
                          latestSectionAttempt = attempt;
                        }
                      }
                    }
                  }
                  
                  if (latestSectionAttempt) {
                    let analytics = {};
                    if (latestSectionAttempt.analytics) {
                      try {
                        analytics = typeof latestSectionAttempt.analytics === 'string' 
                          ? JSON.parse(latestSectionAttempt.analytics) 
                          : latestSectionAttempt.analytics;
                      } catch (e) {
                        analytics = latestSectionAttempt.analytics || {};
                      }
                    }
                    return {
                      response: {
                        analytics: analytics,
                        totalMarksObtained: latestSectionAttempt.total_marks_obtained,
                        status: latestSectionAttempt.status
                      }
                    };
                  }
                }
                return null;
              })()}
            />
          </Box>
        </Box>
        )}
        
        {selectedTestType === 'Reading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
            {(!testToShow.attempts_summary || testToShow.attempts_summary.length === 0) && 
             (!testToShow.sections || testToShow.sections.every(s => !s.attempts || s.attempts.length === 0)) && (
              <Box sx={{ mb: 3, p: 2, borderRadius: '8px', backgroundColor: '#FFF3E0', border: '1px solid #FF9800' }}>
                <Typography variant="body1" sx={{ color: '#F57C00', fontWeight: 500 }}>
                  No attempt data found for this Reading test. If you completed this test, please contact support.
                </Typography>
              </Box>
            )}
            <Box sx={{ mb: 2, width: '100%' }}>
              <ReadingTestUI
              sections={testToShow.sections || []}
              timeLimit={testToShow.time_limit || 60}
              mode="review"
              onComplete={() => {}}
              testId={testToShow.id || null}
              userId={detailsData.response.users && detailsData.response.users[0] ? detailsData.response.users[0].userId || detailsData.response.users[0].user_id : null}
              apiResult={(() => {
                // Create apiResult structure from attempts_summary for review mode
                if (testToShow.attempts_summary && testToShow.attempts_summary.length > 0) {
                  const latestAttempt = testToShow.attempts_summary[0];
                  return {
                    response: {
                      analytics: latestAttempt.analytics || {},
                      totalMarksObtained: latestAttempt.totalMarksObtained
                    }
                  };
                }
                // Fallback: try to get from section attempts
                if (testToShow.sections && testToShow.sections.length > 0) {
                  for (const section of testToShow.sections) {
                    if (section.attempts && section.attempts.length > 0) {
                      const sectionAttempt = section.attempts[0];
                      let analytics = {};
                      if (sectionAttempt.analytics) {
                        try {
                          analytics = typeof sectionAttempt.analytics === 'string' 
                            ? JSON.parse(sectionAttempt.analytics) 
                            : sectionAttempt.analytics;
                        } catch (e) {
                          analytics = sectionAttempt.analytics || {};
                        }
                      }
                      return {
                        response: {
                          analytics: analytics,
                          totalMarksObtained: sectionAttempt.total_marks_obtained
                        }
                      };
                    }
                  }
                }
                return null;
              })()}
            />
          </Box>
        </Box>
        )}
        
        {selectedTestType === 'Writing' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: '100%' }}>
            {(() => {
              // Get latest attempt for pending message check
              let latestAttemptForApi = null;
              let analyticsForApi = {};
              let hasAttempts = false;
              
              if (testToShow.attempts_summary && testToShow.attempts_summary.length > 0) {
                latestAttemptForApi = testToShow.attempts_summary[0];
                hasAttempts = true;
                if (latestAttemptForApi.analytics) {
                  try {
                    analyticsForApi = typeof latestAttemptForApi.analytics === 'string' 
                      ? JSON.parse(latestAttemptForApi.analytics) 
                      : latestAttemptForApi.analytics;
                  } catch (e) {
                    analyticsForApi = latestAttemptForApi.analytics || {};
                  }
                }
              } else if (testToShow.sections && testToShow.sections.length > 0) {
                // Fallback: try to get from section attempts - find latest across all sections
                let latestSectionAttempt = null;
                let latestAttemptDate = null;
                
                for (const section of testToShow.sections) {
                  if (section.attempts && section.attempts.length > 0) {
                    for (const attempt of section.attempts) {
                      // Only consider attempts that match this test
                      if (parseInt(attempt.test_id) === parseInt(testToShow.id)) {
                        const attemptDate = new Date(attempt.created_at || attempt.updated_at);
                        if (!latestAttemptDate || attemptDate > latestAttemptDate) {
                          latestAttemptDate = attemptDate;
                          latestSectionAttempt = attempt;
                        }
                      }
                    }
                  }
                }
                
                if (latestSectionAttempt) {
                  hasAttempts = true;
                  if (latestSectionAttempt.analytics) {
                    try {
                      analyticsForApi = typeof latestSectionAttempt.analytics === 'string' 
                        ? JSON.parse(latestSectionAttempt.analytics) 
                        : latestSectionAttempt.analytics;
                    } catch (e) {
                      analyticsForApi = latestSectionAttempt.analytics || {};
                    }
                  }
                  latestAttemptForApi = {
                    analytics: analyticsForApi,
                    totalMarksObtained: latestSectionAttempt.total_marks_obtained,
                    status: latestSectionAttempt.status
                  };
                }
              }
              
              // Create apiResult structure matching user side format
              const writingApiResult = latestAttemptForApi ? {
                response: {
                  analytics: analyticsForApi,
                  totalMarksObtained: latestAttemptForApi.totalMarksObtained || latestAttemptForApi.total_marks_obtained,
                  status: latestAttemptForApi.status
                }
              } : null;
              
              // Show pending message only if status is actually pending_evaluation (not completed with AI scoring)
              const showPendingMessage = hasAttempts && latestAttemptForApi && 
                                         latestAttemptForApi.status === 'pending_evaluation' &&
                                         !analyticsForApi?.ai_scoring;
              
              return (
                <>
                  {!hasAttempts && (
                    <Box sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: '8px', 
                      backgroundColor: '#FFF3E0',
                      border: '1px solid #FF9800'
                    }}>
                      <Typography variant="body1" sx={{ color: '#F57C00', fontWeight: 500 }}>
                        This Writing section was not attempted in this full test attempt. Showing the test content for review.
                      </Typography>
                    </Box>
                  )}
                  {showPendingMessage && (
                    <Box sx={{ 
                      mb: 2, 
                      p: 2, 
                      borderRadius: '8px', 
                      backgroundColor: '#E3F2FD',
                      border: '1px solid #2196F3'
                    }}>
                      <Typography variant="body1" sx={{ color: '#1976D2', fontWeight: 500 }}>
                        Your writing test has been submitted and is being evaluated. Results will be available soon once your test has been reviewed.
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2, width: '100%' }}>
                    <WritingTestUI
                      sections={testToShow.sections || []}
                      timeLimit={testToShow.time_limit || 60}
                      mode="review"
                      onComplete={() => {}}
                      testId={testToShow.id || null}
                      userId={detailsData.response.users && detailsData.response.users[0] ? detailsData.response.users[0].userId || detailsData.response.users[0].user_id : null}
                      apiResult={writingApiResult}
                      testType={testToShow.test_type}
                    />
                  </Box>
                </>
              );
            })()}
          </Box>
        )}
        
        {selectedTestType === 'Speaking' && (
          <Box sx={{ width: '100%' }}>
            {(() => {
              // Get latest attempt for score display
              let latestAttempt = null;
              if (testToShow.attempts_summary && testToShow.attempts_summary.length > 0) {
                latestAttempt = testToShow.attempts_summary[0];
              } else if (testToShow.sections && testToShow.sections.length > 0) {
                for (const section of testToShow.sections) {
                  if (section.attempts && section.attempts.length > 0) {
                    const matchingAttempt = section.attempts.find(attempt => 
                      parseInt(attempt.test_id) === parseInt(testToShow.id)
                    );
                    if (matchingAttempt) {
                      latestAttempt = matchingAttempt;
                      break;
                    }
                  }
                }
              }
              
              // Create apiResult structure for helper functions
              const speakingApiResult = latestAttempt ? {
                response: {
                  analytics: latestAttempt.analytics,
                  totalMarksObtained: latestAttempt.totalMarksObtained || latestAttempt.total_marks_obtained,
                  status: latestAttempt.status
                }
              } : null;
              
              // Check if score is available (not pending) - handles 0 correctly
              const scoreAvailable = shouldShowScore(speakingApiResult);
              const hasAI = hasAIScore(speakingApiResult);
              
              return (
                <>
                  {(() => {
                    // Check if Speaking was skipped
                    const userData = detailsData?.response?.users?.[0];
                    const isSpeakingOptional = userData?.isSpeakingOptional || false;
                    
                    // Check attempts_summary first
                    let hasAttempts = testToShow.attempts_summary && testToShow.attempts_summary.length > 0;
                    
                    // If no attempts_summary, check section attempts (only count attempts that match this test)
                    if (!hasAttempts && testToShow.sections && testToShow.sections.length > 0) {
                      for (const section of testToShow.sections) {
                        if (section.attempts && section.attempts.length > 0) {
                          // Check if any attempt belongs to this test
                          const matchingAttempt = section.attempts.find(attempt => 
                            parseInt(attempt.test_id) === parseInt(testToShow.id)
                          );
                          if (matchingAttempt) {
                            hasAttempts = true;
                            break;
                          }
                        }
                      }
                    }
                    
                    if (isSpeakingOptional && !hasAttempts) {
                      return (
                        <Box sx={{ 
                          p: 3, 
                          borderRadius: '8px', 
                          backgroundColor: '#FFF3E0',
                          border: '1px solid #FF9800',
                          textAlign: 'center'
                        }}>
                          <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 600, mb: 1 }}>
                            Speaking Test Skipped
                          </Typography>
                          <Typography variant="body1" sx={{ color: '#E65100' }}>
                            Speaking test was skipped in this full test attempt. It was marked as optional.
                          </Typography>
                        </Box>
                      );
                    }
                    
                    // Get latest attempt - try attempts_summary first, then section attempts
                    let latestAttemptForApi = null;
                    let analyticsForApi = {};
                    
                    if (testToShow.attempts_summary && testToShow.attempts_summary.length > 0) {
                      latestAttemptForApi = testToShow.attempts_summary[0];
                      analyticsForApi = latestAttemptForApi.analytics || {};
                    } else if (testToShow.sections && testToShow.sections.length > 0) {
                      // Fallback: try to get from section attempts - find latest across all sections
                      let latestSectionAttempt = null;
                      let latestAttemptDate = null;
                      
                      for (const section of testToShow.sections) {
                        if (section.attempts && section.attempts.length > 0) {
                          for (const attempt of section.attempts) {
                            // Only consider attempts that match this test
                            if (parseInt(attempt.test_id) === parseInt(testToShow.id)) {
                              const attemptDate = new Date(attempt.created_at || attempt.updated_at);
                              if (!latestAttemptDate || attemptDate > latestAttemptDate) {
                                latestAttemptDate = attemptDate;
                                latestSectionAttempt = attempt;
                              }
                            }
                          }
                        }
                      }
                      
                      if (latestSectionAttempt) {
                        if (latestSectionAttempt.analytics) {
                          try {
                            analyticsForApi = typeof latestSectionAttempt.analytics === 'string' 
                              ? JSON.parse(latestSectionAttempt.analytics) 
                              : latestSectionAttempt.analytics;
                          } catch (e) {
                            analyticsForApi = latestSectionAttempt.analytics || {};
                          }
                        }
                        latestAttemptForApi = {
                          analytics: analyticsForApi,
                          totalMarksObtained: latestSectionAttempt.total_marks_obtained,
                          status: latestSectionAttempt.status
                        };
                      }
                    }
                    
                    // Create apiResult structure matching user side format
                    // IMPORTANT: Show responses even if status is pending_evaluation
                    const speakingApiResult = latestAttemptForApi ? {
                      response: {
                        totalMarksObtained: latestAttemptForApi.totalMarksObtained,
                        analytics: analyticsForApi || {},
                        status: latestAttemptForApi.status
                      }
                    } : (hasAttempts ? {
                      response: {
                        totalMarksObtained: null,
                        analytics: analyticsForApi || {},
                        status: 'pending_evaluation'
                      }
                    } : null);

              // Show pending message only if status is actually pending_evaluation (not completed with AI scoring)
              // Parse analytics to check for AI scoring
              let parsedAnalytics = {};
              if (latestAttemptForApi && latestAttemptForApi.analytics) {
                try {
                  parsedAnalytics = typeof latestAttemptForApi.analytics === 'string' 
                    ? JSON.parse(latestAttemptForApi.analytics) 
                    : latestAttemptForApi.analytics;
                } catch (e) {
                  parsedAnalytics = latestAttemptForApi.analytics || {};
                }
              }
              const showPendingMessage = hasAttempts && latestAttemptForApi && 
                                         latestAttemptForApi.status === 'pending_evaluation' &&
                                         !parsedAnalytics?.ai_scoring;
                    
                    return (
                      <>
                        {showPendingMessage && (
                          <Box sx={{ 
                            mb: 2, 
                            p: 2, 
                            borderRadius: '8px', 
                            backgroundColor: '#E3F2FD',
                            border: '1px solid #2196F3'
                          }}>
                            <Typography variant="body1" sx={{ color: '#1976D2', fontWeight: 500 }}>
                              Your speaking recordings have been submitted and are being evaluated. Results will be available soon once your recordings have been reviewed.
                            </Typography>
                          </Box>
                        )}

                        {!hasAttempts && !isSpeakingOptional && (
                          <Box sx={{ 
                            mb: 2, 
                            p: 2, 
                            borderRadius: '8px', 
                            backgroundColor: '#FFF3E0',
                            border: '1px solid #FF9800'
                          }}>
                            <Typography variant="body1" sx={{ color: '#F57C00', fontWeight: 500 }}>
                              This Speaking section was not attempted in this full test attempt. Showing the test content for review.
                            </Typography>
                          </Box>
                        )}
                        
                        <SpeakingTestUI
                          sections={testToShow.sections || []}
                          mode="review"
                          apiResult={speakingApiResult}
                        />
                      </>
                    );
                  })()}
                </>
              );
            })()}
          </Box>
        )}
      </>
    );
  };




  const renderTestScoresBanner = () => {
    if (type !== 'full' || !detailsData?.response?.users?.[0]?.tests) return null;

    const fullUser = detailsData.response.users[0];
    const tests = fullUser.tests;
    const isSpeakingOptionalForAttempt = !!fullUser?.isSpeakingOptional;
    const testScores = {
      Listening: null,
      Reading: null,
      Writing: null,
      Speaking: null
    };

    // Extract scores for each test type
    tests.forEach(test => {
      const testType = test.category_name || test.test_type;
      if (!testType || !testScores.hasOwnProperty(testType)) return;

      if (
        testType === 'Speaking' &&
        isSpeakingOptionalForAttempt &&
        test.category_attempt_status === 'pending_attempt'
      ) {
        testScores[testType] = 'Skipped';
        return;
      }

      if (test.category_attempt_status === 'pending_attempt') {
        testScores[testType] = 'Pending attempt';
        return;
      }

      // First, try to get score from attempts_summary
      if (test.attempts_summary && test.attempts_summary.length > 0) {
        const latestAttempt = test.attempts_summary[0];
        // Check status first - if pending_evaluation, show "Pending" regardless of marks
        if (latestAttempt.status === 'pending_evaluation') {
          testScores[testType] = 'Pending';
          return;
        }
        // Then check marks - use helper to handle 0 correctly
        const attemptApiResult = {
          response: {
            analytics: latestAttempt.analytics,
            totalMarksObtained: latestAttempt.totalMarksObtained,
            status: latestAttempt.status
          }
        };
        const bandScore = getBandScore(attemptApiResult);
        if (bandScore !== null) {
          testScores[testType] = bandScore;
          return;
        }
      }

      // Fallback: Check section-level attempts - find latest across all sections
      if (test.sections && test.sections.length > 0) {
        let latestSectionAttempt = null;
        let latestAttemptDate = null;
        
        for (const section of test.sections) {
          if (section.attempts && section.attempts.length > 0) {
            for (const attempt of section.attempts) {
              // Only consider attempts that match this test
              if (parseInt(attempt.test_id) === parseInt(test.id)) {
                const attemptDate = new Date(attempt.created_at || attempt.updated_at);
                if (!latestAttemptDate || attemptDate > latestAttemptDate) {
                  latestAttemptDate = attemptDate;
                  latestSectionAttempt = attempt;
                }
              }
            }
          }
        }
        
        if (latestSectionAttempt) {
          // Check status first - if pending_evaluation, show "Pending" regardless of marks
          if (latestSectionAttempt.status === 'pending_evaluation') {
            testScores[testType] = 'Pending';
            return;
          }
          // Then check marks
          if (latestSectionAttempt.total_marks_obtained !== null && latestSectionAttempt.total_marks_obtained !== undefined && latestSectionAttempt.total_marks_obtained !== '' && latestSectionAttempt.total_marks_obtained !== '0') {
            testScores[testType] = latestSectionAttempt.total_marks_obtained;
            return;
          }
        }
      }

      // If no attempts found, mark as not attempted
      if (testScores[testType] === null) {
        testScores[testType] = 'N/A';
      }
    });

    return (
      <TestScoresBanner>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#5E35B1' }}>
          Test Section Scores
        </Typography>
        <Grid container spacing={2} alignItems="stretch">
          {Object.entries(testScores).map(([testType, score]) => (
            <Grid item xs={6} sm={3} key={testType} sx={{ display: 'flex' }}>
              <ScoreItem>
                <Typography variant="subtitle2" sx={{ color: '#5E35B1', fontWeight: 500, mb: 0.5 }}>
                  {testType}
                </Typography>
                <Typography variant="h5" sx={{ 
                  color: score === 'Pending' || String(score).toLowerCase().includes('pending') ? '#FF9800' : score === 'Skipped' ? '#F57C00' : score === 'N/A' ? '#9E9E9E' : '#996CFE', 
                  fontWeight: 700,
                  fontSize: typeof score === 'string' && String(score).length > 8 ? { xs: '0.9rem', sm: '1rem' } : undefined,
                  lineHeight: 1.2
                }}>
                  {score !== null ? score : 'N/A'}
                </Typography>
              </ScoreItem>
            </Grid>
          ))}
        </Grid>
      </TestScoresBanner>
    );
  };

  if (loading) {
    return (
      <>
        {!isAdminMode && <Navbar />}
        <PageContainer maxWidth="lg">
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
            <Box textAlign="center">
              <CircularProgress size={60} sx={{ color: '#996CFE', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Loading test details...
              </Typography>
            </Box>
          </Box>
        </PageContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {!isAdminMode && <Navbar />}
        <PageContainer maxWidth="lg">
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          
          {!isAdminMode && (
            <Box textAlign="center">
              <ActionButton
                variant="outlined"
                onClick={handleBackToResults}
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  borderColor: '#996CFE',
                  color: '#996CFE',
                  '&:hover': { 
                    borderColor: '#7C4DFF',
                    backgroundColor: '#F5F0FF'
                  },
                  mr: 2
                }}
              >
                Back to Results
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={handleReturnToDashboard}
                startIcon={<HomeIcon />}
                sx={{ 
                  backgroundColor: '#996CFE',
                  '&:hover': { backgroundColor: '#7C4DFF' }
                }}
              >
                Return to Dashboard
              </ActionButton>
            </Box>
          )}
        </PageContainer>
      </>
    );
  }

  return (
    <>
      {!isAdminMode && <Navbar />}
      <PageContainer maxWidth="lg">
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          {!isAdminMode && (
            <IconButton onClick={handleBackToResults} color="primary">
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h4" component="h1" sx={{ color: '#996CFE', fontWeight: 600, flex: 1 }}>
            {getTestTitle()}
          </Typography>

          {!isAdminMode &&
            type === 'full' &&
            detailsData?.response?.users?.[0]?.fullTestAttempt?.status === 'in_progress' && (
              <ActionButton
                variant="contained"
                onClick={handleResumeFullTest}
                disabled={resumeFullTestLoading}
                startIcon={
                  resumeFullTestLoading ? (
                    <CircularProgress size={18} color="inherit" aria-hidden />
                  ) : null
                }
                sx={{
                  minWidth: { xs: '100%', sm: 200 },
                  backgroundColor: '#996CFE',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#7C4DFF' },
                  '&:disabled': { backgroundColor: '#996CFE', color: '#fff', opacity: 0.72 },
                  ...(resumeFullTestLoading && { pointerEvents: 'none' }),
                }}
              >
                {resumeFullTestLoading ? 'Preparing resume…' : 'Attempt Pending Sections'}
              </ActionButton>
            )}

          {/* TODO: DELETE IELTS RESULT BUTTON WILL BE ADDED HERE */}
          {/* {!isAdminMode && canDeleteResult && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteResultClick}
            >
              Delete result
            </Button>
          )} */}
        </Box>

        <Dialog open={deleteConfirm} onClose={handleCloseDeleteConfirm}>
          <DialogTitle>Delete IELTS result?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will permanently delete this {type === 'full' ? 'full test' : 'test'} result. This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteConfirm} disabled={deleting}>Cancel</Button>
            <Button
              onClick={handleConfirmDeleteResult}
              color="error"
              variant="contained"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>

        {renderTestScoresBanner()}
        {renderTestContent()}

        {!isAdminMode && (
          <Box sx={{ mt: 1 }}>
            <Box display="flex" gap={2} justifyContent="center">
              <ActionButton
                variant="outlined"
                onClick={handleBackToResults}
                startIcon={<ArrowBackIcon />}
                sx={{ 
                  borderColor: '#996CFE',
                  color: '#996CFE',
                  '&:hover': { 
                    borderColor: '#7C4DFF',
                    backgroundColor: '#F5F0FF'
                  }
                }}
              >
                Back to Results
              </ActionButton>
              <ActionButton
                variant="contained"
                onClick={handleReturnToDashboard}
                startIcon={<HomeIcon />}
                sx={{ 
                  backgroundColor: '#996CFE',
                  '&:hover': { backgroundColor: '#7C4DFF' }
                }}
              >
                Return to Dashboard
              </ActionButton>
            </Box>
          </Box>
        )}
      </PageContainer>
    </>
  );
};

export default IELTSTestDetails;